import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { database } from "../firebase/firebase";
import { ref, push, onValue, update, remove } from "firebase/database";
import "../styles/main.css";

export default function Customers() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("list"); // Default to list view
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    gst: "",
    address: "",
    email: "",
    state: "",
    city: "",
    pincode: "",
    billingAddress: "",
    shippingAddress: "",
    paymentTerms: "Due on Receipt",
    creditLimit: "",
    openingBalance: 0,
    balanceType: "receivable",
    customerType: "regular",
    notes: "",
    gstType: "registered",
    pan: "",
    contactPerson: "",
    alternateMobile: "",
    masterType: "sundryDebtor", // sundryDebtor, sundryCreditor, bankAccount
    // Bank account fields
    bankName: "",
    accountNumber: "",
    accountHolderName: "",
    ifscCode: "",
    accountType: "bank" // bank or cash
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch masters from Firebase
  useEffect(() => {
    const customersRef = ref(database, "customers");
    
    const unsubscribe = onValue(customersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const customerList = Object.entries(data).map(([id, customer]) => ({
          id,
          ...customer
        }));
        // Sort by name or latest first
        customerList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCustomers(customerList);
      } else {
        setCustomers([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Validate form based on master type
  const validateForm = () => {
    const newErrors = {};

    // Common validation for all types
    if (!formData.name.trim()) {
      newErrors.name = "Master name is required";
    }

    // Bank Account specific validation - ONLY bank name is compulsory
    if (formData.masterType === "bankAccount") {
      if (!formData.bankName.trim()) {
        newErrors.bankName = "Bank name is required";
      }
      // Optional validations - only validate if field has value
      if (formData.mobile && !/^[0-9]{10}$/.test(formData.mobile)) {
        newErrors.mobile = "Enter valid 10-digit mobile number";
      }
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Enter valid email address";
      }
      if (formData.accountNumber && formData.accountNumber.length < 5) {
        newErrors.accountNumber = "Account number should be at least 5 characters";
      }
      if (formData.ifscCode && formData.ifscCode.length < 11) {
        newErrors.ifscCode = "Enter valid IFSC code";
      }
    } 
    // Sundry Debtor/Creditor specific validation
    else {
      if (formData.mobile && !/^[0-9]{10}$/.test(formData.mobile)) {
        newErrors.mobile = "Enter valid 10-digit mobile number";
      }
      if (formData.gst && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/.test(formData.gst)) {
        newErrors.gst = "Enter valid GST number";
      }
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Enter valid email address";
      }
      if (formData.pincode && !/^[0-9]{6}$/.test(formData.pincode)) {
        newErrors.pincode = "Enter valid 6-digit pincode";
      }
    }

    return newErrors;
  };

  // Generate master ID
  const generateCustomerId = () => {
    const prefix = formData.masterType === "sundryDebtor" ? "SD" :
                   formData.masterType === "sundryCreditor" ? "SC" : "BA";
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${prefix}${timestamp}${random}`;
  };

  // Get master type display name
  const getMasterTypeDisplay = (type) => {
    switch(type) {
      case "sundryDebtor":
        return "Sundry Debtor";
      case "sundryCreditor":
        return "Sundry Creditor";
      case "bankAccount":
        return "Bank Account";
      default:
        return "Sundry Debtor";
    }
  };

  // Save master to database
  const saveCustomer = async () => {
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const customerRef = ref(database, "customers");
      const customerId = generateCustomerId();
      const timestamp = new Date().toISOString();

      const customerData = {
        ...formData,
        customerId,
        createdAt: timestamp,
        updatedAt: timestamp,
        totalPurchases: 0,
        totalPaid: 0,
        lastTransactionDate: null,
        status: "active",
        masterTypeDisplay: getMasterTypeDisplay(formData.masterType)
      };

      // Handle balance only for non-bank accounts
      if (formData.masterType !== "bankAccount") {
        customerData.balance = formData.openingBalance * (formData.balanceType === "payable" ? -1 : 1);
        customerData.billingAddress = formData.billingAddress || formData.address;
        customerData.shippingAddress = formData.shippingAddress || formData.address;
        customerData.gstDetails = {
          type: formData.gstType,
          number: formData.gst,
          pan: formData.pan
        };
      } else {
        // For bank accounts, set balance to 0 and remove unnecessary fields
        customerData.balance = 0;
        // Remove optional fields if not provided
        if (!customerData.accountNumber) delete customerData.accountNumber;
        if (!customerData.accountHolderName) delete customerData.accountHolderName;
        if (!customerData.ifscCode) delete customerData.ifscCode;
        // Remove unnecessary fields
        delete customerData.billingAddress;
        delete customerData.shippingAddress;
        delete customerData.gstDetails;
        delete customerData.paymentTerms;
        delete customerData.creditLimit;
        delete customerData.city;
        delete customerData.state;
        delete customerData.pincode;
        delete customerData.balanceType;
        delete customerData.gstType;
      }

      // Remove unnecessary fields
      delete customerData.balanceType;
      delete customerData.gstType;

      await push(customerRef, customerData);

      // Show success message
      setSuccessMessage("Master added successfully!");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      // Reset form
      resetForm();

    } catch (error) {
      console.error("Error saving master:", error);
      alert("Failed to save master. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update master
  const updateCustomer = async () => {
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const customerRef = ref(database, `customers/${editingCustomer.id}`);
      const timestamp = new Date().toISOString();

      const customerData = {
        ...formData,
        updatedAt: timestamp,
        masterTypeDisplay: getMasterTypeDisplay(formData.masterType)
      };

      // Handle balance only for non-bank accounts
      if (formData.masterType !== "bankAccount") {
        customerData.balance = formData.openingBalance * (formData.balanceType === "payable" ? -1 : 1);
        customerData.billingAddress = formData.billingAddress || formData.address;
        customerData.shippingAddress = formData.shippingAddress || formData.address;
        customerData.gstDetails = {
          type: formData.gstType,
          number: formData.gst,
          pan: formData.pan
        };
      } else {
        // For bank accounts, set balance to 0 and remove unnecessary fields
        customerData.balance = 0;
        // Remove optional fields if not provided
        if (!customerData.accountNumber) delete customerData.accountNumber;
        if (!customerData.accountHolderName) delete customerData.accountHolderName;
        if (!customerData.ifscCode) delete customerData.ifscCode;
        // Remove unnecessary fields
        delete customerData.billingAddress;
        delete customerData.shippingAddress;
        delete customerData.gstDetails;
        delete customerData.paymentTerms;
        delete customerData.creditLimit;
        delete customerData.city;
        delete customerData.state;
        delete customerData.pincode;
        delete customerData.balanceType;
        delete customerData.gstType;
      }

      // Remove unnecessary fields
      delete customerData.balanceType;
      delete customerData.gstType;
      delete customerData.id;

      await update(customerRef, customerData);

      setSuccessMessage("Master updated successfully!");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      // Exit edit mode
      setEditingCustomer(null);
      resetForm();

    } catch (error) {
      console.error("Error updating master:", error);
      alert("Failed to update master. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete master
  const deleteCustomer = async () => {
    if (!customerToDelete) return;

    try {
      const customerRef = ref(database, `customers/${customerToDelete.id}`);
      await remove(customerRef);

      setSuccessMessage("Master deleted successfully!");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      setShowDeleteModal(false);
      setCustomerToDelete(null);
    } catch (error) {
      console.error("Error deleting master:", error);
      alert("Failed to delete master. Please try again.");
    }
  };

  // Quick add master
  const quickAddCustomer = async () => {
    // Validate based on master type
    if (!formData.name) {
      alert("Master name is required");
      return;
    }

    if (formData.masterType === "bankAccount") {
      if (!formData.bankName) {
        alert("Bank name is required");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const customerRef = ref(database, "customers");
      const customerId = generateCustomerId();
      
      const quickData = {
        name: formData.name,
        customerId,
        masterType: formData.masterType,
        masterTypeDisplay: getMasterTypeDisplay(formData.masterType),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        balance: 0,
        status: "active"
      };

      // Add fields based on master type
      if (formData.masterType === "bankAccount") {
        quickData.bankName = formData.bankName;
        // Optional fields - only add if provided
        if (formData.accountNumber) quickData.accountNumber = formData.accountNumber;
        if (formData.accountHolderName) quickData.accountHolderName = formData.accountHolderName;
        if (formData.ifscCode) quickData.ifscCode = formData.ifscCode;
        if (formData.mobile) quickData.mobile = formData.mobile;
        if (formData.email) quickData.email = formData.email;
        if (formData.address) quickData.address = formData.address;
      } else {
        // Sundry Debtor/Creditor fields
        if (formData.mobile) quickData.mobile = formData.mobile;
        if (formData.gst) quickData.gst = formData.gst;
        if (formData.address) quickData.address = formData.address;
      }
      
      await push(customerRef, quickData);

      setSuccessMessage("Master added successfully!");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
      // Reset only the quick add fields
      setFormData({
        ...formData,
        name: "",
        mobile: "",
        gst: "",
        address: "",
        bankName: "",
        accountNumber: "",
        accountHolderName: "",
        ifscCode: ""
      });
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to add master");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit master
  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    
    // Prepare form data from customer
    const formDataForEdit = {
      name: customer.name || "",
      mobile: customer.mobile || "",
      gst: customer.gst || "",
      address: customer.address || "",
      email: customer.email || "",
      state: customer.state || "",
      city: customer.city || "",
      pincode: customer.pincode || "",
      billingAddress: customer.billingAddress || "",
      shippingAddress: customer.shippingAddress || "",
      paymentTerms: customer.paymentTerms || "Due on Receipt",
      creditLimit: customer.creditLimit || "",
      openingBalance: customer.openingBalance || 0,
      balanceType: customer.balance >= 0 ? "receivable" : "payable",
      customerType: customer.customerType || "regular",
      notes: customer.notes || "",
      gstType: customer.gstDetails?.type || "registered",
      pan: customer.gstDetails?.pan || "",
      contactPerson: customer.contactPerson || "",
      alternateMobile: customer.alternateMobile || "",
      masterType: customer.masterType || "sundryDebtor",
      bankName: customer.bankName || "",
      accountNumber: customer.accountNumber || "",
      accountHolderName: customer.accountHolderName || "",
      ifscCode: customer.ifscCode || "",
      accountType: customer.accountType || "bank"
    };
    
    setFormData(formDataForEdit);
    setActiveTab("add");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      mobile: "",
      gst: "",
      address: "",
      email: "",
      state: "",
      city: "",
      pincode: "",
      billingAddress: "",
      shippingAddress: "",
      paymentTerms: "Due on Receipt",
      creditLimit: "",
      openingBalance: 0,
      balanceType: "receivable",
      customerType: "regular",
      notes: "",
      gstType: "registered",
      pan: "",
      contactPerson: "",
      alternateMobile: "",
      masterType: "sundryDebtor",
      bankName: "",
      accountNumber: "",
      accountHolderName: "",
      ifscCode: "",
      accountType: "bank"
    });
    setErrors({});
  };

  // Filter masters based on search
  const filteredCustomers = customers.filter(customer => 
    customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.mobile?.includes(searchTerm) ||
    customer.customerId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate stats
  const totalCustomers = customers.length;
  const activeCustomers = customers.filter(c => c.status !== "inactive").length;
  const totalOutstanding = customers.reduce((sum, c) => sum + (c.balance || 0), 0);
  const sundryDebtors = customers.filter(c => c.masterType === "sundryDebtor").length;
  const sundryCreditors = customers.filter(c => c.masterType === "sundryCreditor").length;
  const bankAccounts = customers.filter(c => c.masterType === "bankAccount").length;

  return (
    <div className="mobile-app" style={{ paddingBottom: "70px" }}>
      {/* Header with Tabs */}
      <div className="page-header">
        <h2>Masters</h2>
        <div className="tab-bar">
          <button 
            className={`tab ${activeTab === "add" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("add");
              setEditingCustomer(null);
              resetForm();
            }}
          >
            ➕ {editingCustomer ? "Edit Master" : "Add Master"}
          </button>
          <button 
            className={`tab ${activeTab === "list" ? "active" : ""}`}
            onClick={() => setActiveTab("list")}
          >
            📋 Master List ({customers.length})
          </button>
        </div>
      </div>

      {/* Success Message */}
      {showSuccess && (
        <div className="success-alert">
          {successMessage} ✅
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Delete Master</h3>
            <p>Are you sure you want to delete {customerToDelete?.name}?</p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setCustomerToDelete(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn-delete"
                onClick={deleteCustomer}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Master Form */}
      {activeTab === "add" && (
        <div className="form-container">
          {/* Edit Mode Indicator */}
          {editingCustomer && (
            <div className="edit-mode-banner">
              ✏️ Editing: {editingCustomer.name}
              <button 
                className="cancel-edit"
                onClick={() => {
                  setEditingCustomer(null);
                  resetForm();
                }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Master Type Dropdown */}
          <div className="master-type-selector">
            <label className="form-label">Select Master Type *</label>
            <select 
              name="masterType" 
              value={formData.masterType} 
              onChange={handleChange}
              className="master-type-dropdown"
            >
              <option value="sundryDebtor">🏢 Sundry Debtor (Customers who owe money)</option>
              <option value="sundryCreditor">🏭 Sundry Creditor (Suppliers/Vendors we owe)</option>
              <option value="bankAccount">🏦 Bank Account (Bank accounts)</option>
            </select>
          </div>

          {/* Quick Add Section (only show when not editing) */}
          {!editingCustomer && (
            <>
              <div className="quick-add-card">
                <h3>Quick Add</h3>
                <div className="quick-add-form">
                  <input
                    type="text"
                    placeholder="Master Name *"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                  
                  {formData.masterType === "bankAccount" ? (
                    <>
                      <input
                        type="text"
                        placeholder="Bank Name *"
                        value={formData.bankName}
                        onChange={(e) => setFormData({...formData, bankName: e.target.value})}
                      />
                      <input
                        type="text"
                        placeholder="Account Number (Optional)"
                        value={formData.accountNumber}
                        onChange={(e) => setFormData({...formData, accountNumber: e.target.value})}
                      />
                      <input
                        type="text"
                        placeholder="Account Holder Name (Optional)"
                        value={formData.accountHolderName}
                        onChange={(e) => setFormData({...formData, accountHolderName: e.target.value})}
                      />
                      <input
                        type="text"
                        placeholder="IFSC Code (Optional)"
                        value={formData.ifscCode}
                        onChange={(e) => setFormData({...formData, ifscCode: e.target.value})}
                      />
                      <input
                        type="tel"
                        placeholder="Mobile Number (Optional)"
                        value={formData.mobile}
                        onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                        maxLength="10"
                      />
                      <input
                        type="email"
                        placeholder="Email (Optional)"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                      />
                      <input
                        type="text"
                        placeholder="Address (Optional)"
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                      />
                    </>
                  ) : (
                    <>
                      <input
                        type="tel"
                        placeholder="Mobile Number"
                        value={formData.mobile}
                        onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                        maxLength="10"
                      />
                      <input
                        type="text"
                        placeholder="GST Number"
                        value={formData.gst}
                        onChange={(e) => setFormData({...formData, gst: e.target.value})}
                      />
                      <input
                        type="text"
                        placeholder="Address"
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                      />
                    </>
                  )}
                  
                  <button 
                    className="btn-quick-add"
                    onClick={quickAddCustomer}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Adding..." : "Quick Add Master"}
                  </button>
                </div>
              </div>

              <div className="form-divider">
                <span>OR</span>
              </div>
            </>
          )}

          {/* Detailed Form */}
          <div className="detailed-form">
            <h3>{editingCustomer ? "Edit Master Details" : "Add with Complete Details"}</h3>
            
            {/* Basic Information */}
            <div className="form-section">
              <h4>Basic Information</h4>
              
              <div className="form-group required">
                <label>Master Name *</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter master name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? "error" : ""}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              {/* Bank Account Details */}
              {formData.masterType === "bankAccount" && (
                <div className="bank-details-section">
                  <h4>Bank Account Details</h4>
                  <div className="form-group required">
                    <label>Bank Name *</label>
                    <input
                      type="text"
                      name="bankName"
                      placeholder="Enter bank name (e.g., HDFC Bank, SBI, etc.)"
                      value={formData.bankName}
                      onChange={handleChange}
                      className={errors.bankName ? "error" : ""}
                    />
                    {errors.bankName && <span className="error-message">{errors.bankName}</span>}
                    <small className="field-hint">Only bank name is mandatory</small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Account Number (Optional)</label>
                      <input
                        type="text"
                        name="accountNumber"
                        placeholder="Enter account number"
                        value={formData.accountNumber}
                        onChange={handleChange}
                        className={errors.accountNumber ? "error" : ""}
                      />
                      {errors.accountNumber && <span className="error-message">{errors.accountNumber}</span>}
                      <small className="field-hint">Optional field</small>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Account Holder Name (Optional)</label>
                    <input
                      type="text"
                      name="accountHolderName"
                      placeholder="Enter account holder name"
                      value={formData.accountHolderName}
                      onChange={handleChange}
                    />
                    <small className="field-hint">Optional field</small>
                  </div>

                  <div className="form-group">
                    <label>IFSC Code (Optional)</label>
                    <input
                      type="text"
                      name="ifscCode"
                      placeholder="Enter IFSC code"
                      value={formData.ifscCode}
                      onChange={handleChange}
                      className={errors.ifscCode ? "error" : ""}
                    />
                    {errors.ifscCode && <span className="error-message">{errors.ifscCode}</span>}
                    <small className="field-hint">Optional field</small>
                  </div>

                  {/* Optional Contact Fields for Bank Account */}
                  <div className="optional-fields-section">
                    <h4>Optional Contact Information</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Mobile Number</label>
                        <input
                          type="tel"
                          name="mobile"
                          placeholder="10-digit mobile number"
                          value={formData.mobile}
                          onChange={handleChange}
                          maxLength="10"
                          className={errors.mobile ? "error" : ""}
                        />
                        {errors.mobile && <span className="error-message">{errors.mobile}</span>}
                      </div>

                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          placeholder="email@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          className={errors.email ? "error" : ""}
                        />
                        {errors.email && <span className="error-message">{errors.email}</span>}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Address</label>
                      <textarea
                        name="address"
                        placeholder="Enter address (optional)"
                        value={formData.address}
                        onChange={handleChange}
                        rows="2"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Sundry Debtor/Creditor Details */}
              {(formData.masterType === "sundryDebtor" || formData.masterType === "sundryCreditor") && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Category</label>
                      <select name="customerType" value={formData.customerType} onChange={handleChange}>
                        <option value="regular">Regular</option>
                        <option value="wholesale">Wholesale</option>
                        <option value="retail">Retail</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Payment Terms</label>
                      <select name="paymentTerms" value={formData.paymentTerms} onChange={handleChange}>
                        <option value="Due on Receipt">Due on Receipt</option>
                        <option value="Net 7">Net 7 Days</option>
                        <option value="Net 15">Net 15 Days</option>
                        <option value="Net 30">Net 30 Days</option>
                      </select>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="form-section">
                    <h4>Contact Information</h4>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Mobile Number</label>
                        <input
                          type="tel"
                          name="mobile"
                          placeholder="10-digit mobile number"
                          value={formData.mobile}
                          onChange={handleChange}
                          maxLength="10"
                          className={errors.mobile ? "error" : ""}
                        />
                        {errors.mobile && <span className="error-message">{errors.mobile}</span>}
                      </div>

                      <div className="form-group">
                        <label>Alternate Mobile</label>
                        <input
                          type="tel"
                          name="alternateMobile"
                          placeholder="Alternate number"
                          value={formData.alternateMobile}
                          onChange={handleChange}
                          maxLength="10"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          placeholder="email@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          className={errors.email ? "error" : ""}
                        />
                        {errors.email && <span className="error-message">{errors.email}</span>}
                      </div>

                      <div className="form-group">
                        <label>Contact Person</label>
                        <input
                          type="text"
                          name="contactPerson"
                          placeholder="Contact person name"
                          value={formData.contactPerson}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="form-section">
                    <h4>Address Information</h4>
                    
                    <div className="form-group">
                      <label>Default Address</label>
                      <textarea
                        name="address"
                        placeholder="Enter default address"
                        value={formData.address}
                        onChange={handleChange}
                        rows="2"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Billing Address (if different)</label>
                        <textarea
                          name="billingAddress"
                          placeholder="Enter billing address"
                          value={formData.billingAddress}
                          onChange={handleChange}
                          rows="2"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Shipping Address (if different)</label>
                        <textarea
                          name="shippingAddress"
                          placeholder="Enter shipping address"
                          value={formData.shippingAddress}
                          onChange={handleChange}
                          rows="2"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>City</label>
                        <input
                          type="text"
                          name="city"
                          placeholder="City"
                          value={formData.city}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>State</label>
                        <input
                          type="text"
                          name="state"
                          placeholder="State"
                          value={formData.state}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Pincode</label>
                        <input
                          type="text"
                          name="pincode"
                          placeholder="6-digit pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          maxLength="6"
                          className={errors.pincode ? "error" : ""}
                        />
                        {errors.pincode && <span className="error-message">{errors.pincode}</span>}
                      </div>
                    </div>
                  </div>

                  {/* Tax Information */}
                  <div className="form-section">
                    <h4>Tax Information</h4>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>GST Type</label>
                        <select name="gstType" value={formData.gstType} onChange={handleChange}>
                          <option value="registered">Registered</option>
                          <option value="unregistered">Unregistered</option>
                          <option value="consumer">Consumer</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>GST Number</label>
                        <input
                          type="text"
                          name="gst"
                          placeholder="GST number"
                          value={formData.gst}
                          onChange={handleChange}
                          className={errors.gst ? "error" : ""}
                        />
                        {errors.gst && <span className="error-message">{errors.gst}</span>}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>PAN Number</label>
                      <input
                        type="text"
                        name="pan"
                        placeholder="PAN number"
                        value={formData.pan}
                        onChange={handleChange}
                        maxLength="10"
                      />
                    </div>
                  </div>

                  {/* Financial Information */}
                  <div className="form-section">
                    <h4>Financial Information</h4>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Opening Balance (₹)</label>
                        <input
                          type="number"
                          name="openingBalance"
                          placeholder="0.00"
                          value={formData.openingBalance}
                          onChange={handleChange}
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div className="form-group">
                        <label>Balance Type</label>
                        <select name="balanceType" value={formData.balanceType} onChange={handleChange}>
                          <option value="receivable">
                            {formData.masterType === "sundryDebtor" ? "They owe us" : "We owe them"}
                          </option>
                          <option value="payable">
                            {formData.masterType === "sundryDebtor" ? "We owe them" : "They owe us"}
                          </option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Credit Limit (₹)</label>
                        <input
                          type="number"
                          name="creditLimit"
                          placeholder="Credit limit"
                          value={formData.creditLimit}
                          onChange={handleChange}
                          min="0"
                          step="100"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Notes - Available for all types */}
            <div className="form-section">
              <h4>Additional Notes</h4>
              <textarea
                name="notes"
                placeholder="Any additional notes..."
                value={formData.notes}
                onChange={handleChange}
                rows="2"
              />
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button 
                className="btn-primary"
                onClick={editingCustomer ? updateCustomer : saveCustomer}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    {editingCustomer ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  editingCustomer ? "Update Master" : "Add Master with Details"
                )}
              </button>
              
              <button 
                className="btn-secondary"
                onClick={resetForm}
              >
                Clear Form
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Master List */}
      {activeTab === "list" && (
        <div className="customer-list-container">
          <div className="search-bar">
            <input
              type="text"
              placeholder="🔍 Search by name, mobile or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="customer-stats">
            <div className="stat-card">
              <span className="stat-label">Total Masters</span>
              <span className="stat-value">{totalCustomers}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Sundry Debtors</span>
              <span className="stat-value">{sundryDebtors}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Sundry Creditors</span>
              <span className="stat-value">{sundryCreditors}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Bank Accounts</span>
              <span className="stat-value">{bankAccounts}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Outstanding</span>
              <span className="stat-value">₹{totalOutstanding.toFixed(2)}</span>
            </div>
          </div>

          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading masters...</p>
            </div>
          ) : (
            <div className="customer-list">
              {filteredCustomers.length > 0 ? (
                filteredCustomers.map(customer => (
                  <div key={customer.id} className="customer-card">
                    <div className="customer-card-header">
                      <div className="customer-info">
                        <h4>{customer.name}</h4>
                        <span className="customer-id">{customer.customerId}</span>
                      </div>
                      <span className={`master-type-badge ${customer.masterType || 'sundryDebtor'}`}>
                        {getMasterTypeDisplay(customer.masterType)}
                      </span>
                    </div>
                    
                    <div className="customer-details">
                      {customer.mobile && (
                        <div className="detail-item">
                          <span className="detail-label">📱</span>
                          <span>{customer.mobile}</span>
                        </div>
                      )}
                      {customer.gst && (
                        <div className="detail-item">
                          <span className="detail-label">🏷️</span>
                          <span>{customer.gst}</span>
                        </div>
                      )}
                      {customer.bankName && (
                        <div className="detail-item">
                          <span className="detail-label">🏦</span>
                          <span>
                            {customer.bankName}
                            {customer.accountNumber && ` - ${customer.accountNumber}`}
                            {customer.accountHolderName && ` (${customer.accountHolderName})`}
                          </span>
                        </div>
                      )}
                      {customer.balance !== 0 && (
                        <div className={`detail-item balance ${customer.balance > 0 ? 'receivable' : 'payable'}`}>
                          <span className="detail-label">💰</span>
                          <span>
                            Balance: ₹{Math.abs(customer.balance).toFixed(2)}
                            {customer.balance > 0 ? ' (Dr)' : ' (Cr)'}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="customer-actions">
                      <button 
                        className="action-btn edit"
                        onClick={() => handleEdit(customer)}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="action-btn delete"
                        onClick={() => {
                          setCustomerToDelete(customer);
                          setShowDeleteModal(true);
                        }}
                      >
                        🗑️ Delete
                      </button>
                      <button 
                        className="action-btn view"
                        onClick={() => navigate(`/customer/${customer.id}`)}
                      >
                        👁️ View
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  {searchTerm ? (
                    <p>No masters found matching "{searchTerm}"</p>
                  ) : (
                    <>
                      <p>No masters added yet</p>
                      <button 
                        className="btn-primary"
                        onClick={() => setActiveTab("add")}
                      >
                        ➕ Add Your First Master
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

{/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>
      <style>{`
  .mobile-app {
    min-height: 100vh;
    background: #f5f5f5;
    padding-bottom: 70px;
  }

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px 20px;
    background: white;
    border-bottom: 1px solid #eee;
    position: sticky;
    top: 0;
    z-index: 100;
  }
  
  .page-header h2 {
    margin: 0;
    font-size: 20px;
    color: #333;
  }

  .tab-bar {
    display: flex;
    gap: 10px;
    background: #f0f0f0;
    padding: 4px;
    border-radius: 25px;
  }

  .tab {
    padding: 8px 16px;
    background: transparent;
    border: none;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .tab.active {
    background: #5a60ea;
    color: white;
    box-shadow: 0 2px 4px rgba(90, 96, 234, 0.3);
  }

  .tab:hover:not(.active) {
    background: #e0e0e0;
    color: #333;
  }

  /* Success Alert */
  .success-alert {
    background: #4caf50;
    color: white;
    padding: 12px 16px;
    margin: 10px 20px;
    border-radius: 8px;
    text-align: center;
    font-weight: 500;
    animation: slideDown 0.3s ease;
  }

  /* Modal Overlay */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-content {
    background: white;
    border-radius: 12px;
    padding: 24px;
    width: 90%;
    max-width: 320px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .modal-content h3 {
    margin: 0 0 12px 0;
    color: #333;
    font-size: 18px;
  }

  .modal-content p {
    margin: 8px 0;
    color: #666;
    font-size: 14px;
  }

  .modal-content .warning-text {
    color: #ff4444;
    font-size: 12px;
  }

  .modal-actions {
    display: flex;
    gap: 12px;
    margin-top: 20px;
  }

  /* Buttons */
  .btn-primary {
    background: #5a60ea;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-primary:hover:not(:disabled) {
    background: #4a50d8;
    transform: translateY(-1px);
  }

  .btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-secondary {
    background: #f0f0f0;
    color: #333;
    border: 1px solid #ddd;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-secondary:hover {
    background: #e0e0e0;
  }

  .btn-delete {
    background: #ff4444;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  .btn-delete:hover {
    background: #ff3333;
  }

  .btn-quick-add {
    background: #5a60ea;
    color: white;
    border: none;
    padding: 12px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 8px;
  }

  .btn-quick-add:hover {
    background: #4a50d8;
    transform: translateY(-1px);
  }

  /* Form Container */
  .form-container {
    padding: 20px;
  }

  /* Master Type Selector */
  .master-type-selector {
    margin-bottom: 20px;
    background: white;
    border-radius: 12px;
    padding: 16px;
    border: 1px solid #eee;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .master-type-selector .form-label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  .master-type-dropdown {
    width: 100%;
    padding: 12px;
    font-size: 14px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: white;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .master-type-dropdown:hover {
    border-color: #5a60ea;
  }

  .master-type-dropdown:focus {
    outline: none;
    border-color: #5a60ea;
    box-shadow: 0 0 0 2px rgba(90, 96, 234, 0.1);
  }

  /* Quick Add Card */
  .quick-add-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid #eee;
  }

  .quick-add-card h3 {
    margin: 0 0 16px 0;
    font-size: 16px;
    color: #333;
    font-weight: 600;
  }

  .quick-add-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .quick-add-form input,
  .quick-add-form select {
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.2s ease;
  }

  .quick-add-form input:focus,
  .quick-add-form select:focus {
    outline: none;
    border-color: #5a60ea;
    box-shadow: 0 0 0 2px rgba(90, 96, 234, 0.1);
  }

  /* Form Divider */
  .form-divider {
    text-align: center;
    margin: 20px 0;
    position: relative;
  }

  .form-divider::before,
  .form-divider::after {
    content: "";
    position: absolute;
    top: 50%;
    width: 45%;
    height: 1px;
    background: #ddd;
  }

  .form-divider::before {
    left: 0;
  }

  .form-divider::after {
    right: 0;
  }

  .form-divider span {
    background: white;
    padding: 0 10px;
    color: #999;
    font-size: 12px;
  }

  /* Detailed Form */
  .detailed-form {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid #eee;
  }

  .detailed-form h3 {
    margin: 0 0 20px 0;
    font-size: 18px;
    color: #333;
    font-weight: 600;
  }

  /* Form Sections */
  .form-section {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #eee;
  }

  .form-section:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .form-section h4 {
    margin: 0 0 16px 0;
    font-size: 16px;
    color: #5a60ea;
    font-weight: 600;
  }

  /* Bank Details Section */
  .bank-details-section {
    background: #f9f9f9;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    border-left: 3px solid #5a60ea;
  }

  .bank-details-section h4 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  /* Optional Fields Section */
  .optional-fields-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px dashed #ddd;
  }

  .optional-fields-section h4 {
    font-size: 13px;
    font-weight: 600;
    color: #999;
    margin-bottom: 12px;
  }

  /* Form Groups */
  .form-group {
    margin-bottom: 16px;
  }

  .form-group label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #333;
    font-size: 13px;
  }

  .form-group.required label::after {
    content: " *";
    color: #ff4444;
    font-weight: bold;
  }

  .form-group input,
  .form-group select,
  .form-group textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.2s ease;
    font-family: inherit;
  }

  .form-group input:focus,
  .form-group select:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: #5a60ea;
    box-shadow: 0 0 0 2px rgba(90, 96, 234, 0.1);
  }

  .form-group input.error {
    border-color: #ff4444;
    background-color: #fff5f5;
  }

  .error-message {
    color: #ff4444;
    font-size: 11px;
    margin-top: 4px;
    display: block;
  }

  /* Field Hint */
  .field-hint {
    display: block;
    font-size: 11px;
    color: #999;
    margin-top: 4px;
  }

  /* Form Row */
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 12px;
  }

  /* Form Actions */
  .form-actions {
    display: flex;
    gap: 12px;
    margin-top: 24px;
  }

  /* Customer List Container */
  .customer-list-container {
    padding: 20px;
  }

  /* Search Bar */
  .search-bar {
    margin-bottom: 20px;
  }

  .search-bar input {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.2s ease;
  }

  .search-bar input:focus {
    outline: none;
    border-color: #5a60ea;
    box-shadow: 0 0 0 2px rgba(90, 96, 234, 0.1);
  }

  /* Customer Stats */
  .customer-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 12px;
    margin-bottom: 20px;
  }

  .stat-card {
    background: white;
    padding: 12px;
    border-radius: 8px;
    text-align: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    border: 1px solid #eee;
  }

  .stat-label {
    display: block;
    font-size: 11px;
    color: #666;
    margin-bottom: 4px;
    font-weight: 500;
    text-transform: uppercase;
  }

  .stat-value {
    display: block;
    font-size: 20px;
    font-weight: bold;
    color: #5a60ea;
  }

  /* Customer Cards */
  .customer-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .customer-card {
    background: white;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    border: 1px solid #eee;
    transition: all 0.2s ease;
  }

  .customer-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .customer-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .customer-info h4 {
    margin: 0 0 4px 0;
    color: #333;
    font-size: 16px;
    font-weight: 600;
  }

  .customer-id {
    font-size: 11px;
    color: #999;
    font-family: monospace;
  }

  /* Master Type Badges */
  .master-type-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .master-type-badge.sundryDebtor {
    background: #e3f2fd;
    color: #1976d2;
  }

  .master-type-badge.sundryCreditor {
    background: #fff3e0;
    color: #f57c00;
  }

  .master-type-badge.bankAccount {
    background: #e8f5e9;
    color: #388e3c;
  }

  /* Customer Details */
  .customer-details {
    margin-bottom: 12px;
  }

  .detail-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #666;
    margin-bottom: 6px;
  }

  .detail-label {
    font-size: 14px;
    min-width: 24px;
  }

  .detail-item.balance.receivable {
    color: #4caf50;
    font-weight: 600;
  }

  .detail-item.balance.payable {
    color: #ff4444;
    font-weight: 600;
  }

  /* Customer Actions */
  .customer-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #eee;
  }

  .action-btn {
    flex: 1;
    padding: 8px;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .action-btn.edit {
    background: #e3f2fd;
    color: #1976d2;
  }

  .action-btn.edit:hover {
    background: #bbdef5;
  }

  .action-btn.delete {
    background: #ffebee;
    color: #ff4444;
  }

  .action-btn.delete:hover {
    background: #ffcdd2;
  }

  .action-btn.view {
    background: #f5f5f5;
    color: #666;
  }

  .action-btn.view:hover {
    background: #e0e0e0;
  }

  /* Empty State */
  .empty-state {
    text-align: center;
    padding: 40px 20px;
    background: white;
    border-radius: 12px;
    border: 1px solid #eee;
  }

  .empty-state p {
    color: #666;
    margin-bottom: 16px;
  }

  /* Loading Spinner */
  .loading-spinner {
    text-align: center;
    padding: 40px;
  }

  .spinner {
    display: inline-block;
    width: 40px;
    height: 40px;
    border: 3px solid #f3f3f3;
    border-top-color: #5a60ea;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Bottom Navigation */
  .bottom-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    display: flex;
    justify-content: space-around;
    padding: 8px 0;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
    z-index: 100;
  }

  .bottom-nav div {
    text-align: center;
    cursor: pointer;
    color: #999;
    font-size: 20px;
    transition: color 0.2s ease;
  }

  .bottom-nav div:hover {
    color: #5a60ea;
  }

  .bottom-nav p {
    margin: 2px 0 0;
    font-size: 11px;
  }

  /* Edit Mode Banner */
  .edit-mode-banner {
    background: #5a60ea;
    color: white;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .cancel-edit {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    padding: 6px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
  }

  .cancel-edit:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  /* Animation */
  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .form-row {
      grid-template-columns: 1fr;
      gap: 12px;
    }
    
    .customer-stats {
      grid-template-columns: repeat(2, 1fr);
    }
    
    .form-actions {
      flex-direction: column;
    }
    
    .btn-primary,
    .btn-secondary {
      width: 100%;
    }
    
    .tab {
      font-size: 12px;
      padding: 6px 12px;
    }
    
    .page-header {
      padding: 12px 16px;
    }
    
    .page-header h2 {
      font-size: 18px;
    }
    
    .form-container,
    .customer-list-container {
      padding: 16px;
    }
  }
`}</style>
    </div>
  );
}